import clsx from 'clsx'
import { mediaQueryUp } from 'components/utils/breakpoint'
import createResponsiveClassNameGetter from 'lib/styles/createResponsiveClassNameGetter'
import getResponsiveStyles from 'lib/styles/getResponsiveStyles'
import { readCSSVar, getThemeClassName } from 'lib/theme/themeUtils'
import { rem } from 'polished'
import type { ComponentProps } from 'react'
import { memo, useMemo } from 'react'
import styled, { css } from 'styled-components'

type SheetBackground =
  | 'none'
  | 'critical'
  | 'critical-light'
  | 'normal-background'
  | 'light-background'
  | 'light-background-alt'
  | 'dark-background'
  | 'highlight-primary'
  | 'highlight-tertiary'
  | 'lux-plus'
  // @deprecated — legacy neutral tokens kept as fallback during migration. Will be removed once all PRs are merged.
  | 'neutral-six'
  | 'neutral-seven'
  | 'neutral-eight'
  | `tier-${App.LoyaltyTier}-primary`
  | `tier-${App.LoyaltyTier}-secondary`
  | `tier-${App.LoyaltyTier}-dark`
  | `tier-${App.LoyaltyTier}-light`

const getBackgroundClassName = createResponsiveClassNameGetter<SheetBackground>('background')
const {
  mobile: backgroundsStyle,
  tablet: tabletBackgroundsStyle,
  desktop: desktopBackgroundsStyle,
} = getResponsiveStyles(
  getBackgroundClassName,
  {
    none: css`background-color: transparent;`,
    critical: css`
      background-color: ${props => props.theme.palette.messaging.critical.normalBackground};
      color: ${props => props.theme.palette.messaging.critical.normalContrast};
    `,
    'critical-light': css`
      background-color: ${props => props.theme.palette.messaging.critical.lightBackground};
      color: ${props => props.theme.palette.messaging.critical.lightContrast};
    `,
    // @deprecated — legacy neutral tokens kept as fallback during migration. Will be removed once all PRs are merged.
    'neutral-six': css`background-color: ${props => props.theme.palette.neutral.default.six};`,
    'neutral-seven': css`background-color: ${props => props.theme.palette.neutral.default.seven};`,
    'neutral-eight': css`background-color: ${props => props.theme.palette.neutral.default.eight};`,
    'normal-background': css`background-color: ${props => props.theme.palette.neutral.surface.normalBackground};`,
    'light-background': css`background-color: ${props => props.theme.palette.neutral.surface.lightBackground};`,
    'light-background-alt': css`background-color: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};`,
    'dark-background': css`background-color: ${props => props.theme.palette.neutral.surface.darkBackground};`,
    'highlight-primary': css`background-color: ${props => props.theme.palette.highlight.primary.lightBackground};`,
    'highlight-tertiary': css`background-color: ${props => props.theme.palette.highlight.tertiary.lightBackground};`,
    'lux-plus': css`
      background-color: ${props => props.theme.palette.product.luxPlus.background};
      color: ${props => props.theme.palette.product.luxPlus.contrast};
    `,
    'tier-bronze-primary': css`
      background: ${props => props.theme.palette.product.loyalty.bronze.gradientSecondaryBackground};
      color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
    `,
    'tier-silver-primary': css`
      background: ${props => props.theme.palette.product.loyalty.silver.gradientSecondaryBackground};
      color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
    `,
    'tier-gold-primary': css`
      background: ${props => props.theme.palette.product.loyalty.gold.gradientSecondaryBackground};
      color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
    `,
    'tier-platinum-primary': css`
      background: ${props => props.theme.palette.product.loyalty.platinum.gradientSecondaryBackground};
      color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
    `,
    'tier-bronze-secondary': css`
      background: ${props => props.theme.palette.product.loyalty.bronze.gradientPrimaryBackground};
      color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
    `,
    'tier-silver-secondary': css`
      background: ${props => props.theme.palette.product.loyalty.silver.gradientPrimaryBackground};
      color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
    `,
    'tier-gold-secondary': css`
      background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
      color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
    `,
    'tier-platinum-secondary': css`
      background: ${props => props.theme.palette.product.loyalty.platinum.gradientPrimaryBackground};
      color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
    `,
    'tier-bronze-dark': css`
      background-color: ${props => props.theme.palette.product.loyalty.bronze.darkBackground};
      color: ${props => props.theme.palette.product.loyalty.bronze.darkContrast};
    `,
    'tier-silver-dark': css`
      background-color: ${props => props.theme.palette.product.loyalty.silver.darkBackground};
      color: ${props => props.theme.palette.product.loyalty.silver.darkContrast};
    `,
    'tier-gold-dark': css`
      background-color: ${props => props.theme.palette.product.loyalty.gold.darkBackground};
      color: ${props => props.theme.palette.product.loyalty.gold.darkContrast};
    `,
    'tier-platinum-dark': css`
      background-color: ${props => props.theme.palette.product.loyalty.platinum.darkBackground};
      color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
    `,
    'tier-bronze-light': css`
      background-color: ${props => props.theme.palette.product.loyalty.bronze.lightBackground};
      color: ${props => props.theme.palette.product.loyalty.bronze.lightContrast};
    `,
    'tier-silver-light': css`
      background-color: ${props => props.theme.palette.product.loyalty.silver.lightBackground};
      color: ${props => props.theme.palette.product.loyalty.silver.lightContrast};
    `,
    'tier-gold-light': css`
      background-color: ${props => props.theme.palette.product.loyalty.gold.lightBackground};
      color: ${props => props.theme.palette.product.loyalty.gold.lightContrast};
    `,
    'tier-platinum-light': css`
      background-color: ${props => props.theme.palette.product.loyalty.platinum.lightBackground};
      color: ${props => props.theme.palette.product.loyalty.platinum.lightContrast};
    `,
  },
)
type SheetBorder =
  | 'transparent'
  | 'critical'
  | 'highlight-primary'
  | 'highlight-primary-dark'
  | 'light-foreground'
  | 'normal-contrast'
  | 'normal-foreground'
  // @deprecated — legacy neutral tokens kept as fallback during migration. Will be removed once all PRs are merged.
  | 'neutral-one'
  | 'neutral-five'
  | 'neutral-six'
  | 'light-foreground-alt'
  | 'dark-foreground'
  | 'primary'
  | `tier-${App.LoyaltyTier}`

const getBorderClassName = createResponsiveClassNameGetter<SheetBorder>('border')
const {
  mobile: bordersStyle,
  tablet: tabletBordersStyle,
  desktop: desktopBordersStyle,
} = getResponsiveStyles(
  getBorderClassName,
  {
    transparent: css`border-color: transparent;`,
    critical: css`border-color: ${props => props.theme.palette.messaging.critical.normalForeground};`,
    'highlight-primary': css`border-color: ${props => props.theme.palette.highlight.primary.normalForeground};`,
    'highlight-primary-dark': css`border-color: ${props => props.theme.palette.highlight.primary.darkForeground};`,
    'light-foreground': css`border-color: ${props => props.theme.palette.neutral.surface.lightForeground};`,
    'normal-contrast': css`border-color: ${props => props.theme.palette.neutral.surface.normalContrast};`,
    'normal-foreground': css`border-color: ${props => props.theme.palette.neutral.surface.normalForeground};`,
    'light-foreground-alt': css`border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};`,
    'dark-foreground': css`border-color: ${props => props.theme.palette.neutral.surface.darkForeground};`,
    // @deprecated — legacy neutral tokens kept as fallback during migration. Will be removed once all PRs are merged.
    'neutral-one': css`border-color: ${props => props.theme.palette.neutral.default.one};`,
    'neutral-five': css`border-color: ${props => props.theme.palette.neutral.default.five};`,
    'neutral-six': css`border-color: ${props => props.theme.palette.neutral.default.six};`,
    primary: css`border-color: ${props => props.theme.palette.brand.primary.normal};`,
    'tier-bronze': css`border-color: ${props => props.theme.palette.product.loyalty.bronze.normalForeground};`,
    'tier-silver': css`border-color: ${props => props.theme.palette.product.loyalty.silver.normalForeground};`,
    'tier-gold': css`border-color: ${props => props.theme.palette.product.loyalty.gold.normalForeground};`,
    'tier-platinum': css`border-color: ${props => props.theme.palette.product.loyalty.platinum.normalForeground};`,
  },
)

type SheetShadow =
  | 'bottom-small'
  | 'bottom-medium'
  | 'bottom-large'
  | 'flat-small'
  | 'flat-large'
  | 'top-small'
  | 'top-medium'
  | 'top-large'

const getShadowClassName = createResponsiveClassNameGetter<SheetShadow>('shadow')
const {
  mobile: shadowsStyle,
  tablet: tabletShadowsStyle,
  desktop: desktopShadowsStyle,
} = getResponsiveStyles(
  getShadowClassName,
  {
    'bottom-small': css`box-shadow: ${props => props.theme.shadow.bottom.small};`,
    'bottom-medium': css`box-shadow: ${props => props.theme.shadow.bottom.medium};`,
    'bottom-large': css`box-shadow: ${props => props.theme.shadow.bottom.large};`,
    'flat-small': css`box-shadow: ${props => props.theme.shadow.flat.small};`,
    'flat-large': css`box-shadow: ${props => props.theme.shadow.flat.large};`,
    'top-small': css`box-shadow: ${props => props.theme.shadow.top.small};`,
    'top-medium': css`box-shadow: ${props => props.theme.shadow.top.medium};`,
    'top-large': css`box-shadow: ${props => props.theme.shadow.top.large};`,
  },
)

const getBorderRadiusClassName = createResponsiveClassNameGetter<App.Design.BorderRadius>('border-radius')
const {
  mobile: borderRadiiStyle,
  tablet: tabletBorderRadiiStyle,
  desktop: desktopBorderRadiiStyle,
} = getResponsiveStyles(
  getBorderRadiusClassName,
  {
    XS: css`border-radius: ${props => props.theme.borderRadius.XS};`,
    S: css`border-radius: ${props => props.theme.borderRadius.S};`,
    M: css`border-radius: ${props => props.theme.borderRadius.M};`,
    L: css`border-radius: ${props => props.theme.borderRadius.L};`,
    round: css`border-radius: ${props => props.theme.borderRadius.round};`,
  },
)

const Root = styled.div`
  box-shadow: none;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  padding: var(--sheet-padding);
  transition: background-color 200ms, border-color 200ms, border-radius 200ms, box-shadow 200ms;

  &.bordered { border: var(--sheet-border-w) solid transparent; }

  ${backgroundsStyle}
  ${bordersStyle}
  ${shadowsStyle}
  ${borderRadiiStyle}

  ${mediaQueryUp.tablet} {
    padding: ${readCSSVar('--sheet-tablet-padding', '--sheet-padding')};

    ${tabletBackgroundsStyle}
    ${tabletBordersStyle}
    ${tabletShadowsStyle}
    ${tabletBorderRadiiStyle}
  }

  ${mediaQueryUp.desktop} {
    padding: ${readCSSVar('--sheet-desktop-padding', '--sheet-tablet-padding', '--sheet-padding')};

    ${desktopBackgroundsStyle}
    ${desktopBordersStyle}
    ${desktopShadowsStyle}
    ${desktopBorderRadiiStyle}
  }
`

type SheetPadding = number
  | `${number} ${number}`
  | `${number} ${number} ${number}`
  | `${number} ${number} ${number} ${number}`

interface Props extends ComponentProps<typeof Root> {
  /**
   * Background colour of the sheet
   */
  background?: SheetBackground
  tabletBackground?: SheetBackground
  desktopBackground?: SheetBackground
  /**
   * Adds a box-shadow to the sheet, use this to make it look 'raised'
   */
  shadow?: SheetShadow
  tabletShadow?: SheetShadow
  desktopShadow?: SheetShadow
  border?: SheetBorder
  tabletBorder?: SheetBorder
  desktopBorder?: SheetBorder
  /**
   * Sets the width of the border when `border` is provided
   *
   * @default 1
   */
  borderWidth?: 1 | 2
  borderRadius?: App.Design.BorderRadius
  tabletBorderRadius?: App.Design.BorderRadius
  desktopBorderRadius?: App.Design.BorderRadius
  /**
   * Sets the palette for the sheet and it's contents at mobile and up sizes
   */
  paletteType?: App.Design.PaletteType
  /**
   * Sets the palette for the sheet and it's contents at tablet and up sizes
   */
  tabletPaletteType?: App.Design.PaletteType
  /**
   * Sets the palette for the sheet and it's contents at desktop and up sizes
   */
  desktopPaletteType?: App.Design.PaletteType
  /**
   * Apply padding to the sheet. Same format as the CSS 'padding' value
   *
   * @example 20 40 = 20 top, 20 bottom, 40 left, 40 right
   */
  padding?: SheetPadding
  /**
   * Apply padding to the sheet at tablet sizes and up. Same format as the CSS 'padding' value
   *
   * @example 20 40 = 20 top, 20 bottom, 40 left, 40 right
   *
   * @default padding
   */
  tabletPadding?: SheetPadding
  /**
   * Apply padding to the sheet at desktop sizes and up. Same format as the CSS 'padding' value
   *
   * @example 20 40 = 20 top, 20 bottom, 40 left, 40 right
   *
   * @default tabletPadding
   */
  desktopPadding?: SheetPadding
}

function getPadding(padding: SheetPadding | undefined): string | undefined {
  if (!padding) {
    return '0'
  }

  if (typeof padding === 'number') {
    return rem(padding)
  }

  if (typeof padding === 'string') {
    // we need to convert numbers to rem
    const parts = padding.split(' ')
    return parts.map(p => rem(+p)).join(' ')
  }
}

function Sheet({
  background,
  tabletBackground,
  desktopBackground,
  border,
  tabletBorder,
  desktopBorder,
  borderWidth = 1,
  borderRadius,
  tabletBorderRadius,
  desktopBorderRadius,
  children,
  className,
  desktopPadding,
  desktopPaletteType,
  padding,
  paletteType,
  shadow,
  tabletShadow,
  desktopShadow,
  tabletPadding,
  tabletPaletteType,
  ...rest
}: Props) {
  const paddingCss = useMemo(() => getPadding(padding), [padding])
  const tabletPaddingCss = useMemo(() => getPadding(tabletPadding ?? padding), [tabletPadding, padding])
  const desktopPaddingCss = useMemo(() => getPadding(desktopPadding ?? tabletPadding ?? padding), [desktopPadding, tabletPadding, padding])

  return <Root
    {...rest}
    className={clsx(
      className,
      getThemeClassName({ mobile: paletteType, tablet: tabletPaletteType, desktop: desktopPaletteType }),
      getBackgroundClassName({ mobile: background, tablet: tabletBackground, desktop: desktopBackground }),
      getShadowClassName({ mobile: shadow, tablet: tabletShadow, desktop: desktopShadow }),
      getBorderRadiusClassName({ mobile: borderRadius, tablet: tabletBorderRadius, desktop: desktopBorderRadius }),
      getBorderClassName({ mobile: border, tablet: tabletBorder, desktop: desktopBorder }),
      { bordered: !!(border ?? tabletBorder ?? desktopBorder) },
    )}
    style={{
      ...rest.style,
      '--sheet-padding': paddingCss,
      '--sheet-tablet-padding': tabletPaddingCss,
      '--sheet-desktop-padding': desktopPaddingCss,
      '--sheet-border-w': rem(borderWidth),
    }}
  >
    {children}
  </Root>
}

export default memo(Sheet)
